import { useState, useMemo } from "react";
import { formatINR } from "@/lib/units";
import ResultsPanel, { CalcPageHeader, Field } from "@/components/ResultsPanel";
import EduSection from "@/components/EduSection";

export default function PropertyTax() {
  const [area, setArea] = useState("1200");
  const [rate, setRate] = useState("6");
  const [category, setCategory] = useState("residential");

  const mult = category === "residential" ? 1 : category === "commercial" ? 2 : 1.5;
  const annual = (Number(area) || 0) * (Number(rate) || 0) * mult;
  const rows = [
    ["Built-up Area", `${area} sq ft`],
    ["Rate ₹/sq ft/year", rate],
    ["Category multiplier", `× ${mult}`],
    ["Annual Property Tax", formatINR(annual)],
    ["Half-yearly", formatINR(annual/2)],
  ];

  return (
    <div>
      <CalcPageHeader title="Property Tax Estimator" description="Ball-park annual house/property tax by built-up area and use category." slug="property-tax" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7">
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Built-up Area (sq ft)"><input data-testid="ptax-area" type="number" value={area} onChange={(e)=>setArea(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
              <Field label="Rate ₹/sq ft/year"><input data-testid="ptax-rate" type="number" value={rate} onChange={(e)=>setRate(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
            </div>
            <Field label="Category">
              <div className="grid grid-cols-3 gap-2">
                {["residential","commercial","industrial"].map((c)=>(
                  <button key={c} data-testid={`ptax-cat-${c}`} onClick={()=>setCategory(c)} className={`h-10 rounded-md text-xs font-medium border transition capitalize ${category===c?"border-primary bg-primary/10 text-primary":"border-border bg-background hover:bg-accent"}`}>{c}</button>
                ))}
              </div>
            </Field>
          </div>
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <ResultsPanel title="Property Tax" slug="property-tax" highlight={{ label: "Annual Tax", value: formatINR(annual) }} rows={rows} note="Estimate only. Actual tax varies by municipality (MCD, BMC, GHMC etc.), zone and rebates." onReset={()=>setArea("")} />
        </div>
      </div>
      <EduSection
        howItWorks={<p>Municipal property tax is generally: <em>Built-up area × Unit Area Value × Use factor × Occupancy factor</em>. This tool gives a linear approximation to help you plan.</p>}
        faqs={[{q:"Is this the exact tax?",a:"No — every municipality has its own formula and rebates. Use this for planning only."}]}
        related={["stamp-duty","rental-yield","registry"]}
      />
    </div>
  );
}
